// NOMBASE — Frontend bootstrap.
//
// Load order (strict, sequential):
//   1. Fetch /api/config/public — backend Doppler-hydrated public keys.
//   2. Init Sentry with the resolved DSN — instrumentation hooks attach
//      before any other module-level code throws.
//   3. Init PostHog with the resolved API key — autocapture / replay
//      starts capturing from this point onward.
//   4. Mount the React tree.
//
// Authentication: removed (May 2026). The app calls `/api/auth/me` on
// mount; if it 401s, AuthContext triggers `/api/auth/dev-bootstrap`
// which mints a session cookie for a hardcoded operator user. No
// sign-in screen is ever shown — users land directly on the
// dashboard.
//
// Total cold-boot cost: a single round-trip to the backend (~30-150ms
// on prod). The backend serves /api/config/public from in-process env
// — no DB hit. We accept ~150ms of un-instrumented boot in exchange
// for never having to bake REACT_APP_* vars into the bundle again.
//
// All init points have graceful no-op fallbacks: missing keys produce
// empty SDKs, never crashes. Network failure on /api/config/public
// falls through to an empty config so the app still renders.
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { PostHogProvider } from 'posthog-js/react';
import '@/index.css';

import { loadRuntimeConfig, _setResolved } from '@/lib/runtime-config';
import { initSentry } from './sentry-init';
import posthog, { initPostHog, isPosthogEnabled } from '@/lib/posthog';
import { isChunkLoadError } from '@/lib/lazyWithRetry';
import App from '@/App';

function CrashFallback({ error } = {}) {
  // A stale-chunk failure after a deploy is usually recoverable — pull the
  // fresh bundle instead of stranding the user on the black screen. To avoid
  // an infinite reload loop when the asset is genuinely gone, we cap the
  // number of reloads within a rolling window and back off exponentially
  // between attempts; once the budget is spent we fall through to the
  // visible fallback instead of looping.
  const isChunk = isChunkLoadError(error);
  const [retriesExhausted, setRetriesExhausted] = React.useState(false);
  React.useEffect(() => {
    if (!isChunk) return undefined;
    const KEY = 'nb_chunk_reload';
    const MAX_RETRIES = 3;
    const WINDOW_MS = 60000; // rolling window the retry budget applies to
    let timer = null;
    try {
      const now = Date.now();
      let state;
      try { state = JSON.parse(window.sessionStorage.getItem(KEY) || 'null'); }
      catch { state = null; }
      // Start a fresh budget on the first failure, or once the window has
      // elapsed since the first reload of the current burst.
      if (!state || typeof state.n !== 'number' || !state.first || now - state.first > WINDOW_MS) {
        state = { n: 0, first: now };
      }
      if (state.n >= MAX_RETRIES) {
        // Budget spent — stop reloading and show the fallback instead.
        setRetriesExhausted(true);
        return undefined;
      }
      // Exponential backoff before each reload: 0s, 1s, 2s … capped at 8s.
      const delay = state.n === 0 ? 0 : Math.min(2 ** (state.n - 1) * 1000, 8000);
      window.sessionStorage.setItem(KEY, JSON.stringify({ n: state.n + 1, first: state.first }));
      timer = window.setTimeout(() => window.location.reload(), delay);
    } catch {
      // No storage (private mode / hardened browser) — don't risk a blind
      // reload loop; show the fallback UI instead.
      setRetriesExhausted(true);
    }
    return () => { if (timer) window.clearTimeout(timer); };
  }, [isChunk]);

  // Blank for the brief moment before the reload commits — but once the
  // retry budget is exhausted, fall through to the visible fallback.
  if (isChunk && !retriesExhausted) return null;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#000',
      color: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      padding: '24px',
    }}>
      <h1 style={{ fontSize: '20px', marginBottom: '12px', fontWeight: 600 }}>
        Something went wrong
      </h1>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginBottom: '24px' }}>
        Our team has been notified. Please reload to try again.
      </p>
      <button
        onClick={() => window.location.assign('/')}
        style={{
          padding: '10px 24px',
          borderRadius: '999px',
          background: '#fff',
          color: '#000',
          border: 'none',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  );
}

async function bootstrap() {
  const cfg = await loadRuntimeConfig();
  _setResolved(cfg);

  // 2. Sentry first so the next two inits' errors are captured.
  initSentry({ dsn: cfg.sentry_dsn });

  // 3. PostHog — autocapture / replay activates from here.
  initPostHog({
    key: cfg.posthog_key,
    host: cfg.posthog_host,
    sessionRecording: cfg.posthog_session_recording,
    sessionRecordingSampleRate: cfg.posthog_session_recording_sample_rate,
    autocapture: cfg.posthog_autocapture,
  });

  // 4. Mount React.
  // Auth removed (May 2026): no `<ClerkProvider/>`. Identity is
  // hydrated by the backend's `/api/auth/dev-bootstrap` shim — the
  // first `/api/auth/me` 401 triggers the bootstrap POST in
  // AuthContext.loadIdentity, mints a `nombase_session` cookie for a
  // hardcoded operator user, and the next probe succeeds. The app
  // lands directly on the dashboard, no sign-in surface.
  //
  // SECURITY NOTE: This makes the app open-access. Anyone with the
  // URL becomes the operator. Acceptable for preview / single-tenant
  // production; do NOT deploy this layout in any multi-tenant
  // setting without restoring an auth layer.
  const root = ReactDOM.createRoot(document.getElementById('root'));
  const tree = (
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={({ error }) => <CrashFallback error={error} />}>
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
  root.render(
    isPosthogEnabled()
      ? <PostHogProvider client={posthog}>{tree}</PostHogProvider>
      : tree
  );
}

// Fire-and-forget — bootstrap rejects only on `loadRuntimeConfig` itself
// throwing, which it doesn't (it always resolves with at least EMPTY).
bootstrap();
